import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { FiLock, FiArrowRight, FiZap, FiAlertTriangle } from "react-icons/fi";
import { useLanguage } from "../../contexts/LanguageContext";

export default function UpgradeCTA({ reason = "quota_exceeded", feature = "", current = 0, quota = 0, planName = "Free", inline = false, onClose = null }) {
  const navigate = useNavigate();
  const { t } = useLanguage();

  const configs = {
    quota_exceeded: { Icon: FiLock, iconColor: "text-red-400", bgColor: "bg-red-500/10", borderColor: "border-red-500/30",
      title: `${feature} — ${t("limit_reached")}`,
      description: `${current}/${quota} ${feature.toLowerCase()} — ${planName}`,
      cta: t("upgrade_to_continue") },
    feature_disabled: { Icon: FiLock, iconColor: "text-yellow-400", bgColor: "bg-yellow-500/10", borderColor: "border-yellow-500/30",
      title: `${feature} — ${t("not_available_plan")}`,
      description: `${feature} — ${planName}`,
      cta: t("upgrade_to_unlock") },
    usage_warning: { Icon: FiAlertTriangle, iconColor: "text-yellow-400", bgColor: "bg-yellow-500/10", borderColor: "border-yellow-500/30",
      title: `${t("almost_at_feature_limit")} ${feature} ${t("limit_suffix")}`,
      description: `${current}/${quota} ${feature.toLowerCase()} — ${t("remaining_hint")}`,
      cta: t("upgrade_now") },
  };

  const { Icon, iconColor, bgColor, borderColor, title, description, cta } = configs[reason] || configs.quota_exceeded;

  if (inline) {
    return (
      <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}
        className={`${bgColor} border ${borderColor} rounded-xl p-3 flex items-center justify-between gap-3`}>
        <div className="flex items-center gap-3">
          <Icon className={`${iconColor} text-lg flex-shrink-0`} />
          <div>
            <p className="text-white text-sm font-medium">{title}</p>
            <p className="text-gray-400 text-xs">{description}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => navigate("/admin/plans")}
            className="bg-blue-600 hover:bg-blue-700 text-white text-sm px-4 py-2 rounded-xl transition-colors flex items-center gap-2 whitespace-nowrap">
            <FiZap className="text-xs" /><span>{cta}</span>
          </button>
          {onClose && <button onClick={onClose} className="text-gray-400 hover:text-white transition-colors">✕</button>}
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
      className={`${bgColor} border ${borderColor} rounded-2xl p-8 text-center`}>
      <div className={`${iconColor} text-5xl mb-4 flex justify-center`}><Icon /></div>
      <h3 className="text-2xl font-bold text-white mb-2">{title}</h3>
      <p className="text-gray-400 mb-6 max-w-md mx-auto">{description}</p>

      {(reason === "quota_exceeded" || reason === "usage_warning") && quota > 0 && (
        <div className="mb-6 max-w-sm mx-auto">
          <div className="flex justify-between text-sm mb-2">
            <span className="text-gray-400">{t("current_usage")}</span>
            <span className={`font-medium ${current >= quota ? "text-red-400" : "text-yellow-400"}`}>{current}/{quota}</span>
          </div>
          <div className="w-full bg-gray-700 rounded-full h-2 overflow-hidden">
            <div className={`h-full transition-all duration-500 ${current >= quota ? "bg-red-500" : current >= quota * 0.8 ? "bg-yellow-500" : "bg-green-500"}`}
              style={{ width: `${Math.min((current / quota) * 100, 100)}%` }} />
          </div>
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-3 justify-center">
        <button onClick={() => navigate("/admin/plans")}
          className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-xl font-medium transition-colors flex items-center justify-center gap-2">
          <FiZap /><span>{cta}</span><FiArrowRight />
        </button>
        {onClose && (
          <button onClick={onClose} className="bg-gray-700 hover:bg-gray-600 text-white px-6 py-3 rounded-xl font-medium transition-colors">
            {t("not_now")}
          </button>
        )}
      </div>

      <div className="mt-6 pt-6 border-t border-gray-700">
        <p className="text-gray-400 text-sm mb-3">{t("recommended_for_you")}</p>
        <div className="flex gap-2 justify-center flex-wrap">
          <span className="bg-blue-500/20 text-blue-400 px-3 py-1 rounded-full text-sm">Pro — $49/{t("per_month").replace("/","")}</span>
          <span className="bg-purple-500/20 text-purple-400 px-3 py-1 rounded-full text-sm">Enterprise — $199/{t("per_month").replace("/","")}</span>
        </div>
      </div>
    </motion.div>
  );
}
